import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Descriptions, DescriptionsProps, ModalProps, Result, Space, Spin } from 'antd';
import { DropdownButtonProps } from 'antd/es/dropdown';
import { isAxiosError } from 'axios';

import { LandingPageItem } from './landing-page-item';
import { useDeleteAgsRoleToProcessMap } from './query';

const getDescriptionProps = (record?: LandingPageItem, props?: DescriptionsProps): DescriptionsProps => ({
  bordered: true,
  column: 1,
  items: [
    { key: 'access', children: record?.access.name, label: 'AGS Role' },
    { key: 'process', children: record?.process.name, label: 'DMS Name' },
    { key: 'employeeType', children: record?.employeeType, label: 'Employee Type' },
  ],
  layout: 'horizontal',
  size: 'small',
  ...props,
});

export const useRoleProcessMapActions = () => {
  const [detailsMap, setDetailsMap] = useState<LandingPageItem | undefined>(undefined);
  const [deleteMap, setDeleteMap] = useState<LandingPageItem | undefined>(undefined);

  const del = useDeleteAgsRoleToProcessMap();
  const navigate = useNavigate();

  const getDeleteModalProps = (props?: ModalProps): ModalProps => {
    const onCancel = () => {
      setDeleteMap(undefined);
      del.reset();
    };

    return {
      children: del.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : del.isSuccess ? (
        <Result status="success" title="Successfully deleted map" />
      ) : del.isError ? (
        <Result
          status="error"
          title="Error occured when deleting map"
          subTitle={isAxiosError(del.error) ? del.error.response?.data['message'] : del.error}
        />
      ) : (
        <Descriptions {...getDescriptionProps(deleteMap)} />
      ),
      centered: true,
      title: 'Are you sure you want to delete this map?',
      cancelButtonProps: { style: del.isSuccess ? { display: 'none' } : undefined, disabled: del.isPending },
      okButtonProps: {
        icon: del.isSuccess ? <CloseCircleOutlined /> : <DeleteOutlined />,
        disabled: del.isPending,
      },
      okText: del.isIdle || del.isPending ? 'Delete' : del.isError ? 'Retry' : 'Close',
      okType: del.isSuccess ? undefined : 'danger',
      open: !!deleteMap,
      width: 800,
      onCancel,
      onOk: () => {
        if (del.isIdle || del.isError) {
          deleteMap && del.mutate(deleteMap.id);
        } else {
          onCancel();
        }
      },
      ...props,
    };
  };

  const getDetailsModalProps = (props?: ModalProps): ModalProps => {
    return {
      cancelText: 'Close',
      children: <Descriptions {...getDescriptionProps(detailsMap)} />,
      centered: true,
      okButtonProps: {
        icon: <DeleteOutlined />,
      },
      okText: 'Delete',
      okType: 'danger',
      open: !!detailsMap,
      title: 'Map Details',
      width: 1100,
      onCancel: () => setDetailsMap(undefined),
      onOk: () => {
        setDeleteMap(detailsMap);
        setDetailsMap(undefined);
      },
      ...props,
    };
  };

  const getDropdownButtonProps = (record: LandingPageItem, props?: DropdownButtonProps): DropdownButtonProps => ({
    children: (
      <Space>
        <EyeOutlined />
        Details
      </Space>
    ),
    menu: {
      items: [
        { label: 'Edit Process', key: 'process', icon: <EditOutlined /> },
        { label: 'Delete Map', key: 'delete', icon: <DeleteOutlined />, danger: true },
      ],
      onClick: (e) => {
        switch (e.key) {
          case 'process':
            navigate(`/admin/process/${record.processId}`);
            break;
          case 'delete':
            setDeleteMap(record);
            break;
          default:
            console.log(`Cannot handle key: ${e.key}.`);
            break;
        }
      },
    },
    size: 'small',
    onClick: () => setDetailsMap(record),
  });

  return { getDeleteModalProps, getDetailsModalProps, getDropdownButtonProps };
};
